import { Alert, Button, Card, EmptyState } from '@playerbase/ui';

import {
  EVENT_CAP,
  type BuilderError,
  type UsePlaytestBuilderState,
} from '../../../../games/use-playtest-builder-state.js';
import { EventRow } from './event-row.js';

interface EventsCardProps {
  builder: UsePlaytestBuilderState;
}

const ERROR_COPY: Partial<Record<BuilderError, string>> = {
  event_cap_reached: `You have reached the ${EVENT_CAP}-event limit. Remove an event before adding another.`,
  question_cap_reached: 'Events can have at most 5 questions (Discord modal cap).',
  field_cap_reached: 'Events can have at most 5 questions (Discord modal cap).',
  nps_already_present: 'Only one NPS question per event.',
  select_options_min_one: 'Select questions need at least one option.',
  select_options_max_25: 'Select options are capped at 25.',
};

function makeClientId(): string {
  return `client-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function EventsCard({ builder }: EventsCardProps): JSX.Element {
  const atCap = builder.state.events.length >= EVENT_CAP;

  function handleAddEvent(): void {
    builder.addEvent(makeClientId());
  }

  return (
    <Card>
      <Card.Header>
        <Card.Title>Events</Card.Title>
        <Card.Description>
          One event per SDK hook. The bot DMs the tester with a "share feedback"
          button when an event fires; tapping it opens a modal with the
          questions below.
        </Card.Description>
      </Card.Header>
      <Card.Content className="flex flex-col gap-4">
        {builder.state.lastError !== null
        && ERROR_COPY[builder.state.lastError] !== undefined ? (
          <Alert tone="warning">{ERROR_COPY[builder.state.lastError]}</Alert>
        ) : null}

        {builder.state.events.length === 0 ? (
          <div className="rounded-md border border-dashed border-border bg-surface p-6">
            <EmptyState
              title="No events yet"
              description="Add an event - usually one per moment in your game where you'd like to ask the tester something."
              cta={
                <Button variant="primary" onClick={handleAddEvent}>
                  Add first event
                </Button>
              }
            />
          </div>
        ) : (
          <ul className="flex flex-col gap-3">
            {builder.state.events.map((event) => (
              <EventRow key={event.clientId} event={event} builder={builder} />
            ))}
          </ul>
        )}

        <div className="flex justify-end">
          <Button variant="ghost" type="button" onClick={handleAddEvent} disabled={atCap}>
            + Add event
          </Button>
        </div>
      </Card.Content>
    </Card>
  );
}
